import { useState } from "react";
import { Modal } from "@/components/Modal";
import { Button } from "@/components/Button";
import { TextField } from "@/components/Field";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { CALLABLES } from "@/services/callables";
import { publishInputFor, publishability, routeFor, TYPE_KEY, type DocumentRow } from "./documents";

/**
 * Publishing a version.
 *
 * **Irreversible, and the dialog says so before the button is live.** A
 * published version is immutable: correcting a mistake means publishing another
 * one, which every institution it applies to then has to read and accept again.
 * That cost lands on people with classes to teach, so the confirmation is the
 * version number typed by hand rather than a second click.
 *
 * The text is fetched from the public website — the same fetch-and-sanitise
 * path an owner reads through — so what is approved here is what they will be
 * shown. Publishing without looking at it is possible; publishing without the
 * dialog having offered it is not.
 */
export function PublishDialog({
  row,
  open,
  onClose,
  onPublish,
}: {
  row: DocumentRow;
  open: boolean;
  onClose: () => void;
  onPublish: (input: ReturnType<typeof publishInputFor>) => Promise<void>;
}) {
  const t = useT();
  const { locale } = useI18n();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const verdict = publishability(row);
  const route = routeFor(row.entry, locale);
  const input = publishInputFor(row.entry, row.entry.routes.de ? "de" : "en");

  const preview = useCallable<{ url: string; html: string }, { route: string; locale: string }>(
    CALLABLES.previewLegalBody,
    route ? { route, locale } : undefined,
    open && route !== undefined
  );

  const confirmed = typed.trim() === String(verdict.version);

  return (
    <Modal
      open={open}
      onClose={onClose}
      titleKey="legal.publish.title"
      titleParams={{ title: input.title }}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            busy={busy}
            disabled={!verdict.publishable || !confirmed || busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onPublish(input);
                setTyped("");
              } finally {
                setBusy(false);
              }
            }}
          >
            {t("legal.publish.confirm", { version: verdict.version })}
          </Button>
        </>
      }
    >
      <p className="text-sm text-ink">{t("legal.publish.irreversible")}</p>
      <p className="mt-2 text-sm text-ink-soft">{t("legal.publish.consequence")}</p>

      <dl className="mt-5 grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 text-sm">
        <dt className="text-ink-muted">{t("legal.column.type")}</dt>
        <dd className="text-ink">{t(TYPE_KEY[input.type])}</dd>
        <dt className="text-ink-muted">{t("legal.column.version")}</dt>
        <dd className="text-ink">{verdict.version}</dd>
        <dt className="text-ink-muted">{t("legal.column.jurisdictions")}</dt>
        <dd className="text-ink">{input.jurisdictions.join(", ")}</dd>
        <dt className="text-ink-muted">{t("legal.column.segments")}</dt>
        <dd className="text-ink">
          {input.segments.map((segment) => t(`institutions.segment.${segment}`)).join(", ")}
        </dd>
        <dt className="text-ink-muted">{t("legal.column.required")}</dt>
        <dd className="text-ink">{t(input.required ? "common.yes" : "common.no")}</dd>
      </dl>

      <p className="mt-4 text-sm text-ink-muted">{t("legal.publish.fromRegistry")}</p>

      <section className="mt-5">
        <h3 className="text-sm font-medium text-ink">{t("legal.publish.preview")}</h3>
        {preview.loading ? (
          <p className="mt-2 text-sm text-ink-muted">{t("common.loading")}</p>
        ) : preview.failed || !preview.data ? (
          <p className="mt-2 text-sm text-accent">{t("legal.publish.previewUnavailable")}</p>
        ) : (
          <>
            <div
              className="legal-body mt-2 max-h-64 overflow-y-auto rounded border border-paper-deep
                         bg-paper px-4 py-3 text-sm text-ink-soft"
              /*
                The HTML is reduced to an allow-list server-side before it is
                sent — no script, no style, no iframe, and every link
                absolutised against the website. That is the same body an owner
                accepts, so a second sanitiser here would be a second answer to
                a question that has one.
              */
              dangerouslySetInnerHTML={{ __html: preview.data.html }}
            />
            <p className="mt-2 text-xs text-ink-muted">{preview.data.url}</p>
          </>
        )}
      </section>

      {verdict.publishable ? (
        <div className="mt-6">
          <TextField
            labelKey="legal.publish.typeVersion"
            labelParams={{ version: verdict.version }}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
          {typed.length > 0 && !confirmed ? (
            <p className="mt-1 text-sm text-accent">{t("legal.publish.mismatch")}</p>
          ) : null}
        </div>
      ) : (
        <p className="mt-6 rounded border border-paper-deep bg-paper-warm px-4 py-3 text-sm text-ink">
          {t(verdict.reasonKey, { version: verdict.version, registry: row.entry.version })}
        </p>
      )}
    </Modal>
  );
}
